/* carpet-calculator.js — Home Cinema Supply
   Room-size calculator for broadloom carpet product pages.
   Companion to sections/carpet-room-calculator.liquid.

   Pricing model: carpet is sold by the LINEAR YARD off a fixed-width roll.
   You buy the full roll width whatever your room measures, so the cost driver
   is how many full-width runs the room needs — not its square footage. A room
   six inches wider than the roll costs nearly double one that fits in a single
   run, and the UI says so out loud rather than burying it in the total.

   Nothing here writes to the cart directly. It sets the theme's own quantity
   input and injects line-item properties into the theme's own product form, so
   Dawn's add-to-cart path stays the single source of truth.
*/
(function () {
  'use strict';

  var FT_PER_YD = 3;
  var TRIM = 0.05; // squaring / trim loss on every cut

  /* ---------- parsing ---------------------------------------------------- */

  // Accepts: 14 · 14.5 · 14 ft 6 in · 14' 6" · 14ft6 · 14-6 · 166in · 166"
  function parseFeet(raw) {
    if (raw == null) return null;
    var s = String(raw).toLowerCase().trim();
    if (!s) return null;
    s = s.replace(/[”″]/g, '"').replace(/[’′]/g, "'");

    // inches-only
    var inchOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")$/);
    if (inchOnly) {
      var io = parseFloat(inchOnly[1]);
      return io > 0 ? io / 12 : null;
    }

    // feet + optional inches
    var m = s.match(/^(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|'|-)?\s*(?:(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")?)?$/);
    if (!m) return null;
    var ft = parseFloat(m[1]);
    var inch = m[2] !== undefined ? parseFloat(m[2]) : 0;
    if (isNaN(ft) || isNaN(inch)) return null;
    if (inch >= 12) return null;
    var total = ft + inch / 12;
    return total > 0 ? total : null;
  }

  function parsePositive(raw) {
    if (raw == null) return null;
    var n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    return isFinite(n) && n > 0 ? n : null;
  }

  function fmtFeet(ft) {
    var whole = Math.floor(ft + 1e-9);
    var inches = Math.round((ft - whole) * 12);
    if (inches === 12) { whole += 1; inches = 0; }
    return inches ? whole + " ft " + inches + " in" : whole + " ft";
  }

  function money(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ---------- estimating -------------------------------------------------- */

  // Extra length allowance for matching a pattern across a seam.
  function patternFactor(repeatIn) {
    if (!repeatIn || repeatIn <= 0) return 0.05;
    if (repeatIn <= 18) return 0.05;
    if (repeatIn <= 36) return 0.10;
    return 0.15;
  }

  // One rectangular area, laid in full-width runs.
  function estimateArea(widthFt, lengthFt, cfg) {
    var roll = cfg.rollWidthFt;
    var repeatFt = (cfg.patternRepeatIn || 0) / 12;

    // Lay the runs along whichever orientation needs fewer of them. A 20x14
    // room takes 2 runs either way, but a 20x10 takes 1 run if you run the
    // carpet down the length and 2 if you run it across. The two orientations
    // can tie on raw strip-feet, so the tie has to break toward fewer runs —
    // otherwise the same room typed 20x10 quietly costs more than 10x20.
    function option(runs, runLen, strips) {
      var seams = Math.max(0, runs - 1);
      return {
        runs: runs,
        runLengthFt: runLen,
        seams: seams,
        stripsRun: strips,
        patternAllowanceFt: seams * repeatFt,
        linearFt: runs * runLen * (1 + TRIM) + seams * repeatFt
      };
    }
    // Runs spanning the width => the strips lie along the room's length.
    var byWidth = option(Math.ceil(widthFt / roll), lengthFt, 'length');
    var byLength = option(Math.ceil(lengthFt / roll), widthFt, 'width');

    var pick;
    if (byLength.linearFt < byWidth.linearFt - 1e-9) pick = byLength;
    else if (byWidth.linearFt < byLength.linearFt - 1e-9) pick = byWidth;
    else pick = byLength.runs < byWidth.runs ? byLength : byWidth;

    return {
      widthFt: widthFt,
      lengthFt: lengthFt,
      areaSqFt: widthFt * lengthFt,
      runs: pick.runs,
      seams: pick.seams,
      runLengthFt: pick.runLengthFt,
      stripsRun: pick.stripsRun,
      patternAllowanceFt: pick.patternAllowanceFt,
      linearFt: pick.linearFt
    };
  }

  function estimateRooms(areas, cfg) {
    var parts = areas.map(function (a) { return estimateArea(a.width, a.length, cfg); });
    var linearFt = parts.reduce(function (s, p) { return s + p.linearFt; }, 0);
    var areaSqFt = parts.reduce(function (s, p) { return s + p.areaSqFt; }, 0);
    var seams = parts.reduce(function (s, p) { return s + p.seams; }, 0);
    var yards = Math.ceil(linearFt / FT_PER_YD);
    return {
      mode: 'room', parts: parts, areaSqFt: areaSqFt, seams: seams,
      linearFt: linearFt, yards: yards,
      coverageSqFt: yards * FT_PER_YD * cfg.rollWidthFt
    };
  }

  function estimateSqFt(sqft, cfg) {
    var factor = 1 + 0.10 + patternFactor(cfg.patternRepeatIn);
    var withWaste = sqft * factor;
    var perYardSqFt = FT_PER_YD * cfg.rollWidthFt;
    var yards = Math.ceil(withWaste / perYardSqFt);
    return {
      mode: 'sqft', areaSqFt: sqft, wasteFactor: factor, yards: yards,
      coverageSqFt: yards * perYardSqFt
    };
  }

  /* ---------- DOM -------------------------------------------------------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function figure(dl, label, value) {
    var row = el('div', 'crc__figure');
    row.appendChild(el('dt', null, label));
    row.appendChild(el('dd', null, value));
    dl.appendChild(row);
  }

  function noteIcon(kind) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '18'); svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
    var d = kind === 'warn'
      ? 'M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'
      : 'M20 6 9 17l-5-5';
    var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
    return svg;
  }

  function note(list, kind, html) {
    var li = el('li', 'crc__note crc__note--' + kind);
    var ic = el('span', 'crc__note-icon');
    ic.appendChild(noteIcon(kind === 'warn' ? 'warn' : 'good'));
    li.appendChild(ic);
    var p = el('p'); p.innerHTML = html;
    li.appendChild(p);
    list.appendChild(li);
  }

  /* ---------- cart wiring ------------------------------------------------- */

  function findProductForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function setQuantity(form, qty) {
    if (!form) return false;
    var input = form.querySelector('.quantity__input, input[name="quantity"]');
    if (!input) return false;
    // Respect a max the theme may have set from inventory.
    var max = parseInt(input.getAttribute('max'), 10);
    if (isFinite(max) && max > 0 && qty > max) qty = max;
    input.value = String(qty);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  function setProperties(form, props) {
    if (!form) return;
    Object.keys(props).forEach(function (key) {
      var name = 'properties[' + key + ']';
      var field = form.querySelector('input[name="' + CSS.escape(name) + '"][data-crc-prop]');
      if (!props[key]) { if (field) field.remove(); return; }
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.setAttribute('data-crc-prop', '');
        form.appendChild(field);
      }
      field.value = props[key];
    });
  }

  /* ---------- boot -------------------------------------------------------- */

  function init(root) {
    var cfgNode = root.querySelector('[data-crc-product]');
    if (!cfgNode) return;
    var cfg;
    try { cfg = JSON.parse(cfgNode.textContent); } catch (e) { return; }
    if (!cfg || !cfg.rollWidthFt || !cfg.pricePerYard) return;

    var form = root.querySelector('[data-crc-form]');
    var out = root.querySelector('[data-crc-output]');
    var areasWrap = root.querySelector('[data-crc-areas]');
    var addBtn = root.querySelector('[data-crc-add-area]');
    var sqftField = root.querySelector('[data-crc-sqft-field]');
    var roomFields = root.querySelector('[data-crc-room-fields]');
    var modeInputs = Array.prototype.slice.call(root.querySelectorAll('[data-crc-mode]'));
    if (!form || !out) return;

    var productForm = findProductForm();
    var maxAreas = 4;

    function currentMode() {
      var on = modeInputs.filter(function (i) { return i.checked; })[0];
      return on ? on.value : 'room';
    }

    function syncMode() {
      var m = currentMode();
      roomFields.hidden = m !== 'room';
      sqftField.hidden = m !== 'sqft';
    }

    function areaRows() {
      return Array.prototype.slice.call(areasWrap.querySelectorAll('[data-crc-area]'));
    }

    function refreshAreaUi() {
      var rows = areaRows();
      rows.forEach(function (row, i) {
        var lbl = row.querySelector('[data-crc-area-label]');
        if (lbl) lbl.textContent = i === 0 ? 'Main room' : 'Area ' + (i + 1);
        var rm = row.querySelector('[data-crc-remove-area]');
        if (rm) rm.hidden = i === 0;
      });
      addBtn.hidden = rows.length >= maxAreas;
    }

    function addArea() {
      var rows = areaRows();
      if (rows.length >= maxAreas) return;
      var clone = rows[0].cloneNode(true);
      clone.querySelectorAll('input').forEach(function (i) {
        i.value = '';
        i.removeAttribute('id');
        i.removeAttribute('aria-describedby');
      });
      clone.querySelectorAll('label').forEach(function (l) { l.removeAttribute('for'); });
      clone.querySelectorAll('[data-crc-error]').forEach(function (e) {
        e.textContent = ''; e.className = 'crc__error';
      });
      areasWrap.appendChild(clone);
      refreshAreaUi();
      var first = clone.querySelector('input');
      if (first) first.focus();
    }

    areasWrap.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-crc-remove-area]');
      if (!rm) return;
      e.preventDefault();
      var row = rm.closest('[data-crc-area]');
      if (row && areaRows().length > 1) { row.remove(); refreshAreaUi(); }
    });
    addBtn.addEventListener('click', function (e) { e.preventDefault(); addArea(); });
    modeInputs.forEach(function (i) { i.addEventListener('change', syncMode); });
    syncMode();
    refreshAreaUi();

    function showError(node, msg) {
      if (!node) return;
      node.textContent = msg || '';
      node.className = 'crc__error' + (msg ? ' crc__error--on' : '');
    }

    function readAreas() {
      var ok = true, areas = [];
      areaRows().forEach(function (row) {
        var wIn = row.querySelector('[data-crc-input="width"]');
        var lIn = row.querySelector('[data-crc-input="length"]');
        var wErr = row.querySelector('[data-crc-error="width"]');
        var lErr = row.querySelector('[data-crc-error="length"]');
        var w = parseFeet(wIn.value), l = parseFeet(lIn.value);

        // A wholly blank extra area is simply ignored, not an error.
        if (!wIn.value.trim() && !lIn.value.trim() && row !== areaRows()[0]) {
          showError(wErr, ''); showError(lErr, '');
          wIn.removeAttribute('aria-invalid'); lIn.removeAttribute('aria-invalid');
          return;
        }
        if (w == null) { showError(wErr, 'Enter a width, e.g. 14 ft 6 in.'); wIn.setAttribute('aria-invalid', 'true'); ok = false; }
        else { showError(wErr, ''); wIn.removeAttribute('aria-invalid'); }
        if (l == null) { showError(lErr, 'Enter a length, e.g. 20 ft.'); lIn.setAttribute('aria-invalid', 'true'); ok = false; }
        else { showError(lErr, ''); lIn.removeAttribute('aria-invalid'); }
        if (w != null && l != null) areas.push({ width: w, length: l });
      });
      if (!areas.length) ok = false;
      return ok ? areas : null;
    }

    function render(est) {
      var price = est.yards * cfg.pricePerYard;
      var perSqFt = est.areaSqFt > 0 ? price / est.areaSqFt : 0;
      out.innerHTML = '';

      var head = el('div', 'crc__headline');
      var yl = el('span', 'crc__yards-label', 'You need');
      var yv = el('span', 'crc__yards', est.yards + (est.yards === 1 ? ' linear yard' : ' linear yards'));
      var pv = el('span', 'crc__price', money(price, cfg.currency));
      head.appendChild(yl); head.appendChild(yv); head.appendChild(pv);
      out.appendChild(head);

      var dl = el('dl', 'crc__figures');
      figure(dl, 'Your floor area', est.areaSqFt.toFixed(1) + ' sq ft');
      figure(dl, 'Carpet you receive', est.coverageSqFt.toFixed(1) + ' sq ft');
      if (est.mode === 'room') {
        var p0 = est.parts[0];
        figure(dl, 'Full-width runs', String(est.parts.reduce(function (s, p) { return s + p.runs; }, 0)));
        figure(dl, 'Seams', est.seams === 0 ? 'None — single run' : String(est.seams));
        if (p0) figure(dl, 'Strips run the', p0.stripsRun === 'length' ? 'room length' : 'room width');
      } else {
        figure(dl, 'Waste allowance', Math.round((est.wasteFactor - 1) * 100) + '%');
      }
      figure(dl, 'Effective cost', money(perSqFt, cfg.currency) + ' / sq ft');
      figure(dl, 'Price per linear yard', money(cfg.pricePerYard, cfg.currency));
      out.appendChild(dl);

      var notes = el('ul', 'crc__notes');

      // The single most useful thing this tool can tell someone.
      if (est.mode === 'room') {
        est.parts.forEach(function (p, i) {
          var shortSide = Math.min(p.widthFt, p.lengthFt);
          var over = shortSide - cfg.rollWidthFt;
          var name = est.parts.length > 1 ? (i === 0 ? 'Your main room' : 'Area ' + (i + 1)) : 'Your room';
          if (p.runs > 1 && over > 0 && over <= 2) {
            note(notes, 'warn',
              '<strong>' + name + ' is only ' + fmtFeet(over) + ' wider than the roll.</strong> ' +
              'That pushes it to ' + p.runs + ' runs and roughly doubles the carpet. If the layout ' +
              'allows a border, a threshold, or a riser that brings the carpeted span to ' +
              fmtFeet(cfg.rollWidthFt) + ' or less, you save most of that.');
          } else if (p.runs === 1) {
            note(notes, 'good',
              '<strong>' + name + ' fits a single run.</strong> No seams, and no pattern-match waste — ' +
              'the most efficient way to buy this carpet.');
          }
        });
        if (est.seams > 0 && cfg.patternRepeatIn) {
          note(notes, 'good',
            'Included in the figure above: extra length to match the ' +
            cfg.patternRepeatIn + '&Prime; repeat across ' + est.seams +
            (est.seams === 1 ? ' seam' : ' seams') + ', plus 5% for trim and squaring.');
        }
      } else {
        note(notes, 'warn',
          '<strong>This is the rough version.</strong> Square footage alone can’t tell us how many ' +
          'full-width runs your room needs, and that is the single biggest cost driver. ' +
          'Switch to <em>Measure my room</em> for a figure you can actually order from.');
      }
      out.appendChild(notes);

      var wired = setQuantity(productForm, est.yards);
      if (wired) {
        var props = {};
        props[cfg.propLabels.coverage] = est.areaSqFt.toFixed(1) + ' sq ft floor area';
        props[cfg.propLabels.yards] = est.yards + ' linear yards @ ' + fmtFeet(cfg.rollWidthFt) + ' wide';
        if (est.mode === 'room') {
          props[cfg.propLabels.room] = est.parts.map(function (p) {
            return fmtFeet(p.widthFt) + ' × ' + fmtFeet(p.lengthFt);
          }).join(' + ');
          props[cfg.propLabels.seams] = est.seams === 0 ? 'Single run, no seams' : est.seams + ' seam(s)';
        } else {
          props[cfg.propLabels.room] = 'Entered as ' + est.areaSqFt.toFixed(1) + ' sq ft (no dimensions given)';
          props[cfg.propLabels.seams] = 'Not calculated — square-footage estimate';
        }
        setProperties(productForm, props);

        var ok = el('p', 'crc__wired');
        ok.innerHTML = 'Quantity set to <strong>' + est.yards + '</strong> on this page. ' +
          'Your measurements travel with the order so we cut the right piece.';
        out.appendChild(ok);
      } else {
        var manual = el('p', 'crc__wired crc__wired--manual');
        manual.innerHTML = 'Set the quantity to <strong>' + est.yards + '</strong> before adding to cart.';
        out.appendChild(manual);
      }

      var disc = el('p', 'crc__disclaimer',
        'An estimate, not a quote. Final yardage depends on your room’s squareness, doorways, ' +
        'and where the seams land. Send us the dimensions and we’ll check the takeoff before you order — ' +
        'carpet is cut to order and cannot be returned.');
      out.appendChild(disc);

      out.setAttribute('tabindex', '-1');
      out.focus({ preventScroll: true });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var mode = currentMode();
      if (mode === 'sqft') {
        var sqIn = root.querySelector('[data-crc-input="sqft"]');
        var sqErr = root.querySelector('[data-crc-error="sqft"]');
        var v = parsePositive(sqIn.value);
        if (v == null) {
          showError(sqErr, 'Enter your floor area in square feet.');
          sqIn.setAttribute('aria-invalid', 'true');
          return;
        }
        showError(sqErr, ''); sqIn.removeAttribute('aria-invalid');
        render(estimateSqFt(v, cfg));
        return;
      }
      var areas = readAreas();
      if (!areas) return;
      render(estimateRooms(areas, cfg));
    });

    root.classList.add('crc--ready');
  }

  function boot() {
    document.querySelectorAll('[data-carpet-calculator]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target.querySelector('[data-carpet-calculator]');
    if (r) init(r);
  });
})();
